import React from 'react';
import { PixelStreamingCommunicator } from '../../sdk/PixelStreamingCommunicator';

interface TopLeftNavigationClusterProps {
  communicator: PixelStreamingCommunicator;
  buildingView: string;
  apartmentId: string;
  furnitureActive: boolean;
  lightingMood: 'day' | 'evening' | 'night';
  onBuildingViewChange: (view: string) => void;
  onApartmentIdChange: (id: string) => void;
  onFurnitureToggle: () => void;
  onLightingMoodChange: (mood: 'day' | 'evening' | 'night') => void;
  disabled?: boolean;
}

export const TopLeftNavigationCluster: React.FC<TopLeftNavigationClusterProps> = ({
  communicator,
  buildingView,
  apartmentId,
  furnitureActive,
  lightingMood,
  onBuildingViewChange,
  onApartmentIdChange,
  onFurnitureToggle,
  onLightingMoodChange,
  disabled = false
}) => {
  // Get current time
  const getCurrentTime = () => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  };

  const [currentTime, setCurrentTime] = React.useState(getCurrentTime());

  // Update time every minute
  React.useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(getCurrentTime());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const handleBuildingViewChange = (view: string) => {
    onBuildingViewChange(view);
    if (view && communicator) {
      communicator.switchBuildingView(view);
    }
  };

  const handleJumpToApartment = () => {
    if (apartmentId && communicator) {
      communicator.jumpToApartment(apartmentId);
    }
  };

  const handleFurnitureToggle = () => {
    onFurnitureToggle();
    if (communicator) {
      communicator.toggleFurniture();
    }
  };

  const handleLightingMoodChange = (mood: 'day' | 'evening' | 'night') => {
    onLightingMoodChange(mood);
    if (communicator) {
      communicator.setLightingMood(mood);
    }
  };

  return (
    <div className="top-left-navigation-cluster">
      {/* Building View Dropdown */}
      <select
        className="marketing-dropdown"
        value={buildingView}
        onChange={(e) => handleBuildingViewChange(e.target.value)}
        disabled={disabled}
      >
        <option value="">Building View</option>
        <option value="view1">View 1</option>
        <option value="view2">View 2</option>
        <option value="view3">View 3</option>
      </select>

      {/* Apartment Selector Dropdown */}
      <select
        className="marketing-dropdown"
        value={apartmentId}
        onChange={(e) => onApartmentIdChange(e.target.value)}
        disabled={disabled}
      >
        <option value="">Select Apartment</option>
        <option value="W2.12">W2.12</option>
        <option value="W2.13">W2.13</option>
        <option value="W3.01">W3.01</option>
        <option value="W3.02">W3.02</option>
      </select>

      {/* Jump to Apartment Button */}
      <button
        className="marketing-button"
        onClick={handleJumpToApartment}
        disabled={disabled || !apartmentId}
      >
        Jump to Apartment
      </button>

      {/* Furniture Toggle Button */}
      <button
        className={`marketing-toggle-button ${furnitureActive ? 'active' : ''}`}
        onClick={handleFurnitureToggle}
        disabled={disabled}
      >
        Furniture {furnitureActive ? 'Active' : 'Inactive'}
      </button>

      {/* Lighting Mood Dropdown */}
      <select
        className="marketing-dropdown"
        value={lightingMood}
        onChange={(e) => handleLightingMoodChange(e.target.value as 'day' | 'evening' | 'night')}
        disabled={disabled}
      >
        <option value="day">Day</option>
        <option value="evening">Evening</option>
        <option value="night">Night</option>
      </select>

      {/* Time Chip */}
      <div className="marketing-time-chip">
        {currentTime}
      </div>
    </div>
  );
};
